import { Publication, UniversalProcessModel, UpmValidationErrorLevel } from '@/gql/graphql';

import { Changelist } from '@/gql/graphql';
import { SortOption } from '@/hooks/use-maintainer-home/use-maintainer-home.hook.core';
import {
  getBucParentName,
  getConfigurationOperators,
  getNodeName,
  getNodeTailoringOperators,
} from './maintainer.core.selectors';
import { UPMNode, UPMNodeEnum } from './maintainer.core.types';

export const MAINTAINER_HOME_ROUTE = '/maintainer/home';
export const MAINTAINER_HOME_ALL_ROUTE = `${MAINTAINER_HOME_ROUTE}/all`;
export const MAINTAINER_HOME_DO_DETAILS_ROUTE = `${MAINTAINER_HOME_ROUTE}/do/:id`;
export const MAINTAINER_HOME_PUBLISH_DO_ROUTE = `${MAINTAINER_HOME_ROUTE}/publish/:id`;

// TODO: some of these functions are exclusively used on some pages, consider moving them to the respective page files

const getBuSpecificContentNodes = (upm?: UniversalProcessModel): UPMNode[] => upm?.buSpecificContents ?? [];

const getTailoringOperatorNodes = (upm?: UniversalProcessModel): UPMNode[] => [
  ...(upm?.designObligations?.flatMap(getNodeTailoringOperators) ?? []),
  ...(upm?.buildingBlocks?.flatMap(getNodeTailoringOperators) ?? []),
];

const getConfigurationOperatorNodes = (upm?: UniversalProcessModel): UPMNode[] => [
  ...(upm?.designObligations?.flatMap(getConfigurationOperators) ?? []),
  ...(upm?.buildingBlocks?.flatMap(getConfigurationOperators) ?? []),
  ...(upm?.tailoringCriteria?.flatMap(getConfigurationOperators) ?? []),
];

const DIRECT_UPM_NODE_COLLECTION_KEYS = [
  'buildingBlocks',
  'dataBlocks',
  'tailoringCriteria',
  'configurations',
  'informations',
  'businessUnits',
  'maturityStages',
  'competencies',
  'designObligations',
  'maturityLevelSets',
  'maturityLevelSequences',
] as const;

const getUpmNodeCollection = (
  upm: UniversalProcessModel | undefined,
  key: (typeof DIRECT_UPM_NODE_COLLECTION_KEYS)[number],
) => (upm?.[key] ?? []) as unknown as UPMNode[];

const getDirectUpmNodes = (upm?: UniversalProcessModel): UPMNode[] =>
  DIRECT_UPM_NODE_COLLECTION_KEYS.flatMap((key) => getUpmNodeCollection(upm, key));

/**
 * Flattens the Universal Process Model into a single array of UPM nodes.
 *
 * Extracts various nested node arrays including business-specific contents, tailoring operators,
 * building blocks, data blocks, tailoring criteria, configurations, informations, business units,
 * maturity stages, competencies, and design obligations from the UPM, then concatenates them into
 * one flat array for easier processing.
 *
 * @param {UniversalProcessModel} [upm] - The Universal Process Model containing grouped UPM nodes
 * @returns {UPMNode[]} A flattened array containing all UPM nodes from all categories
 *
 * @example
 * // Flatten all nodes for searching or filtering
 * const flatNodes = flattenUPMNodes(upm);
 *
 * @example
 * // Get all nodes to perform operations across different node types
 * const allNodes = flattenUPMNodes(universalProcessModel);
 * const totalNodeCount = allNodes.length;
 *
 * @see {@link getUnpublishedDos} - Uses this function to get all nodes before filtering
 */
export const flattenUPMNodes = (upm?: UniversalProcessModel): UPMNode[] => [
  ...getBuSpecificContentNodes(upm),
  ...getTailoringOperatorNodes(upm),
  ...getConfigurationOperatorNodes(upm),
  ...getDirectUpmNodes(upm),
];

/**
 * Returns UPM nodes that have no associated design obligations.
 *
 * This function first flattens the Universal Process Model into a single array of UPM nodes,
 * then filters out nodes that have one or more referenced design obligations. Useful for
 * identifying orphaned or unconnected nodes in the model.
 *
 * @param {UniversalProcessModel} [upm] - The Universal Process Model to extract nodes from
 * @returns {UPMNode[]} An array of UPM nodes with no referenced design obligations
 *
 * @example
 * // Find all nodes that aren't connected to design obligations
 * const unconnectedNodes = getNodesWithoutDO(upm);
 *
 * @example
 * // Check if there are any orphaned nodes in the system
 * const orphanedNodes = getNodesWithoutDO(universalProcessModel);
 * if (orphanedNodes.length > 0) {
 *   console.log(`Found ${orphanedNodes.length} unconnected nodes`);
 * }
 *
 * @see {@link flattenUPMNodes} - Used internally to get all nodes before filtering
 */
const getNodesWithoutDO = (upm?: UniversalProcessModel) => {
  if (!upm) {
    return [];
  }
  const flatUPM = flattenUPMNodes(upm);
  return flatUPM.filter((node) => {
    return node.referencedDesignObligations?.length === 0;
  });
};

/**
 * Returns the UPM node enumeration value corresponding to the current route path.
 *
 * Extracts the UPM node type from the provided URL path using a regex that matches the segment
 * following "/home/" with an optional "publish/" segment. The function supports both maintainer
 * home routes and publish routes. If no valid segment is found, it defaults to UPMNodeEnum.Unpublished.
 *
 * @param {string} path - The URL path to parse
 * @returns {UPMNodeEnum} The corresponding UPM node enumeration value
 *
 * @example
 * // Parse a publish route
 * const nodeEnum = getEnumFromPath('/home/publish/DesignObligation');
 * // Returns UPMNodeEnum.DesignObligation
 *
 * @example
 * // Parse a regular home route
 * const nodeEnum = getEnumFromPath('/home/BuildingBlock');
 * // Returns UPMNodeEnum.BuildingBlock
 *
 * @example
 * // Invalid or unrecognized path
 * const nodeEnum = getEnumFromPath('/invalid/path');
 * // Returns UPMNodeEnum.Unpublished (default)
 */
export const getEnumFromPath = (path: string): UPMNodeEnum => {
  // This regex optionally matches "publish/" after "/home/".
  const regex = /\/home\/(?:publish\/)?([^/]+)/;
  const match = path.match(regex);
  if (match && match[1]) {
    return match[1] as UPMNodeEnum;
  }
  return UPMNodeEnum.Unpublished;
};

/**
 * Returns the UPM node enumeration value based on the provided node ID.
 *
 * Parses a node ID by splitting on "_" and using the prefix to determine the corresponding
 * UPMNodeEnum value. This is useful for identifying node types from their IDs throughout
 * the application. If the prefix doesn't match any known types, defaults to UPMNodeEnum.Unpublished.
 *
 * @param {string} [id] - The node ID to parse (format: "PREFIX_identifier")
 * @returns {UPMNodeEnum} The corresponding UPMNodeEnum value
 *
 * @example
 * // Parse a design obligation ID
 * const enumValue = getEnumFromId('DO_12345');
 * // Returns UPMNodeEnum.DesignObligation
 *
 * @example
 * // Parse a building block ID
 * const enumValue = getEnumFromId('BB_67890');
 * // Returns UPMNodeEnum.BuildingBlock
 *
 * @example
 * // Handle unknown or invalid ID
 * const enumValue = getEnumFromId('UNKNOWN_123');
 * // Returns UPMNodeEnum.Unpublished (default)
 *
 * @example
 * // Handle undefined ID
 * const enumValue = getEnumFromId(undefined);
 * // Returns UPMNodeEnum.Unpublished (default)
 */
export const getEnumFromId = (id?: string): UPMNodeEnum => {
  const type = id?.split('_')[0];
  switch (type) {
    case 'MTS':
      return UPMNodeEnum.MaturityStages;
    case 'CO':
      return UPMNodeEnum.Competencies;
    case 'BU':
      return UPMNodeEnum.BusinessUnits;
    case 'CFG':
      return UPMNodeEnum.Configuration;
    case 'INF':
      return UPMNodeEnum.Information;
    case 'DO':
      return UPMNodeEnum.DesignObligation;
    case 'BB':
      return UPMNodeEnum.BuildingBlock;
    case 'TC':
      return UPMNodeEnum.TailroingCriteria;
    case 'DB':
      return UPMNodeEnum.DataBlock;
    case 'BUC':
      return UPMNodeEnum.BuSpecificContent;
    default:
      return UPMNodeEnum.Unpublished;
  }
};

/**
 * Maps GraphQL typename strings to their corresponding UPMNodeEnum values.
 * Supports all Universal Process Model node types with unified handling for
 * MaturityLevelSet and MaturityLevelSequence.
 */
const TYPENAME_TO_ENUM_MAP: Partial<Record<string, UPMNodeEnum>> = {
  UniversalDesignObligation: UPMNodeEnum.DesignObligation,
  UniversalMaturityStage: UPMNodeEnum.MaturityStages,
  UniversalCompetency: UPMNodeEnum.Competencies,
  UniversalInformation: UPMNodeEnum.Information,
  BusinessUnit: UPMNodeEnum.BusinessUnits,
  UniversalConfigurationGroup: UPMNodeEnum.Configuration,
  UniversalTailoringCriterion: UPMNodeEnum.TailroingCriteria,
  UniversalBuildingBlock: UPMNodeEnum.BuildingBlock,
  UniversalDataBlock: UPMNodeEnum.DataBlock,
  UniversalBuSpecificContent: UPMNodeEnum.BuSpecificContent,
  UniversalOperator: UPMNodeEnum.Operator,
  UniversalMaturityLevelSet: UPMNodeEnum.MaturityLevelSet,
  UniversalMaturityLevelSequence: UPMNodeEnum.MaturityLevelSet,
};

/**
 * Returns the UPM node enumeration value corresponding to the node's __typename.
 *
 * Maps the GraphQL __typename property of a UPM node to its corresponding UPMNodeEnum value.
 * This is useful when working with GraphQL responses where you need to determine the node type
 * from the returned data. If the typename is not recognized, defaults to UPMNodeEnum.Unpublished.
 *
 * @param {UPMNode} [currentNode] - The UPM node whose __typename is to be mapped
 * @returns {UPMNodeEnum} The corresponding UPMNodeEnum value
 *
 * @example
 * // Map a design obligation node
 * const node = { __typename: 'UniversalDesignObligation', ...otherProps };
 * const enumValue = getEnumFromTypename(node);
 * // Returns UPMNodeEnum.DesignObligation
 *
 * @example
 * // Map a building block node
 * const node = { __typename: 'UniversalBuildingBlock', ...otherProps };
 * const enumValue = getEnumFromTypename(node);
 * // Returns UPMNodeEnum.BuildingBlock
 *
 * @example
 * // Handle undefined node
 * const enumValue = getEnumFromTypename(undefined);
 * // Returns UPMNodeEnum.Unpublished (default)
 */
export const getEnumFromTypename = (currentNode?: UPMNode): UPMNodeEnum => {
  return TYPENAME_TO_ENUM_MAP[currentNode?.__typename as string] ?? UPMNodeEnum.Unpublished;
};

/**
 * Filters nodes to only include those with Modified, Published, or New publication status.
 *
 * @param {UPMNode[]} nodes - Array of UPM nodes to filter
 * @returns {UPMNode[]} Filtered array containing only nodes with Modified, Published, or New status
 *
 * @example
 * const filtered = filterHomeNodes(allNodes);
 * // Returns only nodes with Modified, Published, or New publication status
 */
const filterHomeNodes = (nodes: UPMNode[]): UPMNode[] => {
  return nodes.filter((node) => {
    return (
      node.publication === Publication.Modified ||
      node.publication === Publication.Published ||
      node.publication === Publication.New ||
      node.publication === Publication.Archived
    );
  });
};

/**
 * Returns UPM nodes corresponding to the maintainer home route path.
 *
 * Parses the provided URL path to determine the node type, then retrieves the appropriate
 * nodes from the Universal Process Model. Handles both regular home routes and publish routes.
 * Includes special handling for unpublished design obligations and unconnected nodes.
 * Returns an empty array if either UPM parameter is missing.
 *
 * @param {UniversalProcessModel} [upm] - The Universal Process Model containing node data
 * @param {UniversalProcessModel} [publishedUpm] - The published Universal Process Model (required for operation)
 * @returns {UPMNode[]} An array of UPM nodes corresponding to the route, or empty array if UPMs are missing
 *
 * @example
 * // Get design obligation nodes for a publish route
 * const nodes = getHomeNodes('/home/publish/DesignObligation', upm, publishedUpm);
 * // Returns all design obligation nodes from the UPM
 *
 * @example
 * // Get building block nodes for a regular home route
 * const nodes = getHomeNodes('/home/BuildingBlock', upm, publishedUpm);
 * // Returns all building block nodes from the UPM
 *
 * @example
 * // Get unpublished design obligations for 'Unpublished' route
 * const nodes = getHomeNodes('/home/Unpublished', upm, publishedUpm);
 * // Returns only unpublished design obligation nodes
 *
 * @example
 * // Get unconnected nodes for 'Unconnected' route
 * const nodes = getHomeNodes('/home/Unconnected', upm, publishedUpm);
 * // Returns nodes with no referenced design obligations
 *
 * @see {@link getEnumFromPath} - Used to parse the path into a node type
 * @see {@link getUnpublishedDos} - Used to retrieve unpublished design obligations
 * @see {@link getNodesWithoutDO} - Used to retrieve unconnected nodes
 */
export const getHomeNodes = (upm?: UniversalProcessModel, publishedUpm?: UniversalProcessModel) => {
  if (!upm || !publishedUpm) {
    return [];
  }

  const nodeArrays = [
    upm.designObligations,
    upm.maturityStages,
    upm.dataBlocks,
    upm.buildingBlocks,
    upm.competencies,
    upm.informations,
    upm.businessUnits,
    upm.configurations,
    upm.tailoringCriteria,
    // MLS is the sole top-level row; MLSQ children are rendered inside their parent MLS
    // card by `MlsNodeListItem`, not as separate top-level entries.
    upm.maturityLevelSets,
    upm.buSpecificContents,
  ];
  // we're making sure that each one of the node arrays is defined before proceeding
  return nodeArrays.flatMap((nodes) => filterHomeNodes(nodes ?? []));
};

/**
 * Retrieves all UPM nodes that are not published.
 *
 * This function flattens the Universal Process Model into a single array of nodes
 * and filters out nodes that have a publication status of 'Published'. Returns
 * all nodes that are in draft, pending, or any other non-published state.
 *
 * @param {UniversalProcessModel} [upm] - The Universal Process Model containing all node types
 * @returns {UPMNode[]} An array of UPM nodes where publication status is not 'Published'
 *
 * @example
 * // Get all unpublished nodes from the UPM
 * const unpublishedNodes = getUnpublishedNodes(upm);
 *
 * @example
 * // Returns nodes from all categories (design obligations, building blocks, etc.)
 * // that are not yet published
 * const draftNodes = getUnpublishedNodes(universalProcessModel);
 *
 * @see {@link flattenUPMNodes} - Used internally to flatten the UPM structure
 * @see {@link Publication} - Enum containing publication status values
 */
const getUnpublishedDos = (upm?: UniversalProcessModel) => {
  const flatUPM = flattenUPMNodes(upm);
  const unpublishedNodes = flatUPM
    .filter((node) => {
      return node.publication !== Publication.Published && node.publication !== Publication.Deleted;
    })
    .filter((node) => node.__typename === 'UniversalDesignObligation');
  return unpublishedNodes;
};

/**
 * Compares two ISO timestamps; nodes without a timestamp always sort last, regardless of order.
 * @param {string | null | undefined} a - First timestamp
 * @param {string | null | undefined} b - Second timestamp
 * @param {SortOption['order']} order - Sort direction
 * @returns {number} Comparator result
 */
const compareUpdatedAt = (a: string | null | undefined, b: string | null | undefined, order: SortOption['order']) => {
  if (!a || !b) {
    return Number(!a) - Number(!b);
  }
  return order === 'asc' ? a.localeCompare(b) : b.localeCompare(a);
};

/**
 * Sorts an array of UPM nodes based on the provided sort option.
 *
 * Currently supports sorting by 'name' field only. The nodes' names are compared using
 * localeCompare for proper internationalization support. The sort order (ascending or
 * descending) is determined by the provided sort option. Returns a new array without
 * modifying the original.
 *
 * @param {UPMNode[]} nodes - The array of UPM nodes to sort
 * @param {SortOption} sort - The sort option containing the field and order
 * @returns {UPMNode[]} A new array of sorted UPM nodes (original array is not modified)
 *
 * @example
 * // Sort nodes alphabetically by name (ascending)
 * const sortedNodes = sortNodes(nodes, { field: 'name', order: 'asc' });
 *
 * @example
 * // Sort nodes reverse alphabetically by name (descending)
 * const sortedNodes = sortNodes(nodes, { field: 'name', order: 'desc' });
 *
 * @example
 * // Original array remains unchanged
 * const originalNodes = [nodeB, nodeA, nodeC];
 * const sortedNodes = sortNodes(originalNodes, { field: 'name', order: 'asc' });
 * // originalNodes is still [nodeB, nodeA, nodeC]
 * // sortedNodes is [nodeA, nodeB, nodeC]
 *
 * @see {@link getNodeName} - Used internally to extract node names for comparison
 */
export const sortNodes = (nodes: UPMNode[], sort: SortOption) => {
  // BUC nodes display their parent node's name, so sort by that instead of the BUC's own name
  const getSortName = (node: UPMNode) =>
    node.__typename === 'UniversalBuSpecificContent' ? getBucParentName(node) : getNodeName(node);

  return [...nodes].sort((a, b) => {
    if (sort.field === 'name') {
      const nameA = getSortName(a);
      const nameB = getSortName(b);
      return sort.order === 'asc' ? nameA.localeCompare(nameB) : nameB.localeCompare(nameA);
    }
    if (sort.field === 'updatedAt') {
      return compareUpdatedAt(a.updatedAt, b.updatedAt, sort.order);
    }
    return 0;
  });
};

/**
 * Retrieves validation errors from a changelist that correspond to the provided UPM nodes.
 *
 * Filters the validation errors in the changelist to include only critical ('Must' level) errors
 * that apply to the provided nodes. This is useful for displaying relevant validation issues
 * when working with specific sets of nodes.
 *
 * @param {Changelist} [changelist] - The changelist containing validation errors
 * @param {UPMNode[]} [nodes] - An array of UPM nodes to compare against error node IDs
 * @returns {Array} An array of critical validation errors that apply to the given nodes
 *
 * @example
 * // Get all critical errors for a set of nodes
 * const errors = getNodeErrorsFromChangelist(changelist, selectedNodes);
 *
 * @example
 * // Check if nodes have validation errors before proceeding
 * const designObligationNodes = getSpecificNode(UPMNodeEnum.DesignObligation, upm);
 * const errors = getNodeErrorsFromChangelist(changelist, designObligationNodes);
 * if (errors.length > 0) {
 *   console.log(`Found ${errors.length} critical validation errors`);
 * }
 *
 * @example
 * // Handle empty inputs gracefully
 * const errors = getNodeErrorsFromChangelist(undefined, []);
 * // Returns empty array
 *
 * @see {@link UpmValidationErrorLevel} - Only 'Must' level errors are returned
 */
export const getNodeErrorsFromChangelist = (changelist?: Changelist, nodes?: UPMNode[]) => {
  if (!changelist || !nodes) {
    return [];
  }
  return changelist.errors
    .filter((error) => {
      return nodes.some((node) => node.id === error.nodeId);
    })
    .filter((error) => error.level === UpmValidationErrorLevel.Must);
};

/**
 * Filters out nodes with Deleted or Archived publication status.
 *
 * This function removes nodes that have been marked as deleted or archived from the provided array,
 * ensuring that only active nodes (New, Modified, Published, etc.) are returned. This is commonly
 * used in form configurations and UI components to prevent showing inactive nodes to users.
 *
 * @param {UPMNode[]} nodes - Array of UPM nodes to filter
 * @returns {UPMNode[]} Filtered array containing only nodes with active publication status
 *
 * @example
 * const allNodes = upm.buildingBlocks;
 * const activeNodes = filterDeletedNodes(allNodes);
 * // Returns only nodes that are not deleted or archived
 *
 * @example
 * // Usage in form configurations
 * options: filterDeletedNodes(upm.businessUnits).map((node) => ({
 *   label: getNodeName(node),
 *   value: node.id,
 * }))
 */
export const filterDeletedNodes = (nodes: UPMNode[]) => {
  return nodes.filter((node) => node.publication !== Publication.Deleted && node.publication !== Publication.Archived);
};
